/**
 * CaptureModal — the ONE canonical document-capture modal (Contract C3,
 * brief §2.2 + §1b PDF p5–7, decision D1).
 *
 * Replaces the start-gated PerDocScanModal body + the dead UnifiedCaptureSurface
 * with a single modal that follows the canonical ceremony:
 *
 *   1. Two-tile chooser — **Camera/Scanner | File Upload**. Which device tile
 *      shows depends on mobile vs desktop Teams (mobile → Camera, desktop →
 *      Scanner). The File-Upload tile accepts **drag-drop** AND click→browse.
 *   2. On capture/drop → extraction **auto-STARTS immediately** (no Scan-Now /
 *      Test-Run / Yes-No start gate — D1). Honest single "Reading…" status,
 *      not synthetic stage timers (drift finding #5).
 *   3. **"Detected as …" + type-override dropdown** (PDF p5–7) when override
 *      options are supplied — changing the type re-runs extraction.
 *   4. **Apply STAYS** (D1 + SPEC Screen 14). On Apply the capture is converted
 *      to a single PDF (brief §2.3, multipage preserved) and handed up.
 *
 * Accepts PDF · JPEG · PNG · TIFF · HEIC · HEIF — 4 MB max. HEIC/HEIF/TIFF are
 * transcoded to JPEG by `preflightImageFile` before upload; Azure DI never sees
 * an unsupported type.
 *
 * Presentational + prop-driven (Contract C3) — no shell/review imports. BR
 * mounts it (directly or via DocumentTileGrid / ReviewDropStrip / the
 * PerDocScanModal compat wrapper).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { ModalShell, Button, Banner } from '@/components/primitives';
import { documentIconUrl } from '@/icons/flat-library';
import { DocumentCaptureModule } from '../../scan/DocumentCaptureModule.js';
import { preflightImageFile, PREFLIGHT_ACCEPTED_MIMES, rotateImageBase64 } from '../../../utils/imageConvert.js';
import { pdfFirstPageThumbnailFromBase64 } from '../../../utils/pdfThumbnail.js';
import { mergeCapturesToPdf, type CapturedPage } from './pdfSave.js';
import {
  buildDisplayRows,
  correctionsForEdits,
  type CaptureConflict,
  type ExtractDisplayRow,
} from './extractDisplay.js';

/** Result of an extraction pass. Stable shape shared with the capture pipeline. */
export interface ScanResultPayload {
  success: boolean;
  fields?: Record<string, { value: string | null; confidence: number }>;
  needsConfirm?: ReadonlyArray<string>;
  error?: string;
  /** Raw response for the parent's field-mapping use. */
  raw?: unknown;
  /**
   * Stated-vs-extracted conflicts (NRA smoke test, Bill 2026-06-12). The
   * review pane renders each as a pick-one alert; Apply is gated until every
   * conflict is resolved (North-Star §2.6 — never silent-overwrite).
   */
  conflicts?: ReadonlyArray<CaptureConflict>;
  /**
   * Officer corrections made inline at the Apply step (underlying field key →
   * corrected value). Set by CaptureModal on Apply; capture hosts convert via
   * `correctionsToPersonPatch` so the typed values win over the raw extract.
   */
  corrections?: Record<string, string>;
  /**
   * Bill 2026-10-16 (sideways scans) — clockwise quarter-turn (90/180/270) that
   * makes the captured page upright, from Azure DI's page angle. The modal
   * rotates the page it stores and shows by it.
   */
  uprightRotationDeg?: number;
}

/** One option in the "Detected as …" override dropdown. */
export interface OverrideOption {
  value: string;
  label: string;
}

/** The PDF artifact + originals handed to `onApply`. */
export interface CaptureArtifact {
  /** Single merged PDF (all captured pages) — base64, no data: prefix. */
  pdfBase64: string;
  pdfMimeType: 'application/pdf';
  pageCount: number;
  /** First captured page's original bytes (thumbnails / DI re-runs). */
  originalBase64: string;
  originalMimeType: string;
  /** Original filename when the capture came from a file upload. */
  originalFilename?: string;
}

export interface CaptureModalProps {
  open: boolean;
  /**
   * Review import (Bill 2026-10-06): reopen an earlier capture's review — the stored
   * result (with the corrections already applied) and its artifact. The modal opens
   * on the review step; Apply re-emits the ORIGINAL artifact (all pages) with the new
   * corrections, so a value missed the first time can be fixed without re-scanning.
   */
  reviewOf?: { result: ScanResultPayload; artifact: CaptureArtifact; chosenDocType?: string } | null;
  /** Doc-type label shown in the title (e.g. "Foreign Passport"). */
  docLabel: string;
  docHint?: string;
  /** Document-icon slug for the thumbnail fallback. */
  iconSlug?: string;
  mode?: 'add' | 'edit';
  /**
   * Drag-drop hand-off. When set (a file was dropped on a tile / the review
   * strip), the chooser is skipped and extraction auto-starts on this file.
   */
  initialFile?: File | null;
  /**
   * "Detected as …" override dropdown options. Omit for known-doc-type tiles
   * (the type is already the tile). Supplied by the generic drop flows.
   */
  overrideOptions?: ReadonlyArray<OverrideOption>;
  /** Suggests a detected type from the bytes (salvaged 2-pass classifier). */
  autoClassify?: (base64: string, mimeType: string) => Promise<{ docTypeValue: string; detectedRawType?: string }>;
  /** Extraction dispatch — parent routes to the right Azure DI model. */
  onCapture: (base64: string, mimeType: string, docTypeValue?: string) => Promise<ScanResultPayload>;
  /** Apply (kept per D1). result + PDF artifact + chosen doc-type. */
  onApply: (result: ScanResultPayload, artifact: CaptureArtifact, chosenDocType?: string) => void;
  onClose: () => void;
  maxUploadBytes?: number;
  /** Default true — convert all captures to one PDF on save (brief §2.3). */
  pdfOnSave?: boolean;
  /**
   * Attach-only doc type (no Azure DI reading pass — e.g. the "Other
   * supporting document" tile). Shows the attach note, including the
   * military-ID restriction (18 USC 701 — never imaged or stored).
   */
  attachOnly?: boolean;
}

type Phase = 'choose' | 'extracting' | 'review' | 'saving' | 'failed';
type DeviceMode = 'none' | 'device' | 'upload';

const DEFAULT_MAX_BYTES = 4 * 1024 * 1024;
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.tiff,.tif,.heic,.heif';

/**
 * Multi-step zoom for the review-pane document preview (smoke test #2,
 * Bill 2026-06-12 — book-style passports put the data page in the bottom
 * half; one-step zoom was not enough). Repeated clicks cycle through these
 * steps (wrapping back to 1×); the +/− controls step through them; the
 * mouse wheel zooms continuously between 1× and ZOOM_MAX.
 */
const ZOOM_STEPS: ReadonlyArray<number> = [1, 1.5, 2, 3, 4];
const ZOOM_MAX = 4;

function isMobileUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
  if (ua.indexOf('Macintosh') >= 0 && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/** Extension token → filetype badge variant for the processing hero. */
function fileTypeToken(filename: string | undefined, mimeType: string | undefined): string {
  const ext = (filename?.split('.').pop() ?? '').toLowerCase();
  if (ext) return ext;
  const m = (mimeType ?? '').toLowerCase();
  if (m.includes('pdf')) return 'pdf';
  if (m.includes('png')) return 'png';
  if (m.includes('tif')) return 'tiff';
  if (m.includes('heic') || m.includes('heif')) return 'heic';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
  return 'doc';
}

/**
 * Rich processing "hero" — the di-probe's animated capture interface ported to
 * prod (Bill 2026-06-01). Replaces the plain "Analyzing…" spinner. Shows a live
 * document preview with a scanning sweep, the filename + filetype badge, a large
 * phase status, and a 5-segment progress track. `firstOriginal` provides the
 * thumbnail (image types render directly; PDFs get a doc glyph). The segments +
 * status advance with the real phases prod emits (preflight → extract → done);
 * the remaining segments animate cosmetically during the extract await.
 */
function ProcessingHero({
  phase,
  firstOriginal,
  detectedRawType,
}: {
  phase: Phase;
  firstOriginal: { base64: string; mimeType: string; filename?: string } | null;
  detectedRawType: string | null;
}): JSX.Element {
  const fname = firstOriginal?.filename ?? 'Captured document';
  const ftoken = fileTypeToken(firstOriginal?.filename, firstOriginal?.mimeType);
  const isImage = (firstOriginal?.mimeType ?? '').startsWith('image/');
  const thumbSrc = isImage && firstOriginal ? `data:${firstOriginal.mimeType};base64,${firstOriginal.base64}` : null;

  // Phase → segment index (0-based) the track has reached, + status line.
  const scanning = phase === 'extracting';
  const done = phase === 'saving'; // saving = extraction succeeded, building the PDF
  const failed = phase === 'failed';
  // 5 segments: capture · preflight · classify · extract · done.
  const reached = failed ? 3 : done ? 4 : phase === 'extracting' ? 3 : 1;
  const statusText = failed
    ? 'Could not read the document'
    : done
      ? 'Finishing up…'
      : 'Reading the document with Azure Content Understanding…';

  const heroCls = [
    'pdw-capture-hero',
    thumbSrc ? 'pdw-capture-hero--thumb' : '',
    scanning ? 'pdw-capture-hero--scanning' : '',
    done ? 'pdw-capture-hero--done' : '',
    failed ? 'pdw-capture-hero--failed' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={heroCls} role="status" aria-live="polite">
      <div className="pdw-capture-hero__preview">
        <div className="pdw-capture-hero__sweep" aria-hidden />
        <div className="pdw-capture-hero__doc">
          {thumbSrc
            ? <img src={thumbSrc} alt="" />
            : <span className="pdw-capture-hero__doc-placeholder" aria-hidden>📄</span>}
        </div>
      </div>
      <div className="pdw-capture-hero__main">
        <div className="pdw-capture-hero__fname">
          <span className={`pdw-capture-ftbadge pdw-capture-ftbadge--${ftoken}`}>{ftoken}</span>
          <span className="pdw-capture-hero__nm">{fname}</span>
        </div>
        <div className="pdw-capture-hero__status">{statusText}</div>
        <div className="pdw-capture-hero__sub">
          {detectedRawType ? <>Detected as <b>{detectedRawType}</b></> : <>Identifying document type…</>}
        </div>
        <div className="pdw-capture-hero__track" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={[
                'pdw-capture-hero__seg',
                i < reached ? 'pdw-capture-hero__seg--done' : '',
                i === reached && !done && !failed ? 'pdw-capture-hero__seg--active' : '',
              ].filter(Boolean).join(' ')}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * BE `needsConfirm` entries are PascalCase track-keys ("AddressLine1",
 * "DateOfBirth"); the display rows carry camelCase post-alias member keys
 * ('addressLine1', 'dob'). Normalize so the needs-attention highlight lands
 * on the right rows.
 */
const NEEDS_CONFIRM_ALIAS: Readonly<Record<string, string>> = {
  dateOfBirth: 'dob',
  sex: 'gender',
  idTypeCode: 'idType',
};
function normalizeNeedsConfirmKey(key: string): string {
  const lc = key.charAt(0).toLowerCase() + key.slice(1);
  return NEEDS_CONFIRM_ALIAS[lc] ?? lc;
}

function readFileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/** Preflight (HEIC/TIFF→JPEG, oversize-PDF rasterize, downsample) then return
 *  base64 + final MIME. Intake accepts up to 25 MB (Bill 2026-07-13 Item 14);
 *  preflightImageFile guarantees the OUTPUT fits `maxBytes` (Azure DI cap). */
const INTAKE_MAX_BYTES = 25 * 1024 * 1024;
async function prepareFile(file: File, maxBytes: number): Promise<{ base64: string; mimeType: string; filename: string }> {
  if (file.size > INTAKE_MAX_BYTES) {
    throw new Error(`File too large (max 25 MB). Got ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
  }
  const prepared = await preflightImageFile(file, maxBytes);
  const base64 = await readFileBase64(prepared);
  return { base64, mimeType: prepared.type || 'application/octet-stream', filename: prepared.name };
}

export function CaptureModal({
  open,
  reviewOf,
  docLabel,
  docHint,
  iconSlug,
  mode = 'add',
  initialFile,
  overrideOptions,
  autoClassify,
  onCapture,
  onApply,
  onClose,
  maxUploadBytes = DEFAULT_MAX_BYTES,
  pdfOnSave = true,
  attachOnly = false,
}: CaptureModalProps): JSX.Element | null {
  const [phase, setPhase] = useState<Phase>('choose');
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('none');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResultPayload | null>(null);
  const [detectedRawType, setDetectedRawType] = useState<string | null>(null);
  const [chosenDocType, setChosenDocType] = useState<string>('');
  // All captured pages (multipage docs preserve every page in one PDF — §2.3).
  const [pages, setPages] = useState<CapturedPage[]>([]);
  const [firstOriginal, setFirstOriginal] = useState<{ base64: string; mimeType: string; filename?: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // NRA smoke test (Bill 2026-06-12) — inline corrections keyed by display
  // factId. Editable in the review pane; persisted over the raw extract at
  // Apply (D1: the commit gate is where low-confidence values get fixed).
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Conflict resolutions — the FSR's pick per stated-vs-extracted conflict.
  const [conflictPicks, setConflictPicks] = useState<Record<string, 'stated' | 'extracted'>>({});
  // Enlarged side-by-side preview of the captured document (image direct;
  // PDF first page rendered via the existing pdf.js util). Blob URLs are
  // revoked on replace/unmount via previewBlobRef.
  const [previewUrl, setPreviewUrl] = useState<string>('');
  // Multi-step zoom + drag-to-pan for the preview (smoke test #2,
  // Bill 2026-06-12). zoom 1 = fit; pan offsets are screen pixels applied
  // OUTSIDE the scale so drag deltas map 1:1.
  const [zoom, setZoom] = useState<number>(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  // Header-× / Escape / scrim discard-confirm (the ModalShell confirmClose
  // guard, hosted locally since the capture modal renders its own header
  // bar with the relocated action buttons — semantics unchanged).
  const [pendingClose, setPendingClose] = useState<boolean>(false);
  const previewBlobRef = useRef<string>('');
  const zoomViewportRef = useRef<HTMLDivElement | null>(null);
  const zoomImgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    moved: boolean;
  } | null>(null);
  // A drag that actually moved must not also fire the click-to-zoom cycle.
  const suppressClickRef = useRef<boolean>(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const isMobile = useRef<boolean>(isMobileUA()).current;
  // Tracks whether the first page has been captured (extraction runs once, on
  // page 1; subsequent "Add page" captures only append to the merged PDF).
  const firstCapturedRef = useRef<boolean>(false);

  // Review import: seed the review step from the earlier capture once per open.
  useEffect(() => {
    if (!open || !reviewOf || firstCapturedRef.current) return;
    firstCapturedRef.current = true;
    const a = reviewOf.artifact;
    setPages([{ base64: a.originalBase64, mimeType: a.originalMimeType }]);
    setFirstOriginal({ base64: a.originalBase64, mimeType: a.originalMimeType, ...(a.originalFilename ? { filename: a.originalFilename } : {}) });
    if (reviewOf.chosenDocType) setChosenDocType(reviewOf.chosenDocType);
    setResult(reviewOf.result);
    setPhase('review');
  }, [open, reviewOf]);

  const reset = useCallback((): void => {
    setPhase('choose');
    setDeviceMode('none');
    setError(null);
    setResult(null);
    setDetectedRawType(null);
    setChosenDocType('');
    setPages([]);
    setFirstOriginal(null);
    setDragOver(false);
    setEdits({});
    setConflictPicks({});
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsPanning(false);
    setPendingClose(false);
    firstCapturedRef.current = false;
    dragRef.current = null;
    suppressClickRef.current = false;
  }, []);

  // Build the enlarged compare-pane preview whenever the captured bytes
  // change. Images render directly from a data URL; PDFs render their first
  // page through pdf.js (existing pdfThumbnail util — no new dependency).
  // HEIC/HEIF/TIFF never reach here as-is (preflight transcodes to JPEG).
  useEffect(() => {
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = '';
    }
    if (!firstOriginal) {
      setPreviewUrl('');
      return;
    }
    if (firstOriginal.mimeType.startsWith('image/')) {
      setPreviewUrl(`data:${firstOriginal.mimeType};base64,${firstOriginal.base64}`);
      return;
    }
    if (firstOriginal.mimeType === 'application/pdf') {
      let cancelled = false;
      setPreviewUrl('');
      void pdfFirstPageThumbnailFromBase64(firstOriginal.base64, { width: 1000, quality: 0.92 })
        .then((url) => {
          if (cancelled) {
            if (url) URL.revokeObjectURL(url);
            return;
          }
          previewBlobRef.current = url;
          setPreviewUrl(url);
        });
      return () => { cancelled = true; };
    }
    setPreviewUrl('');
    return;
  }, [firstOriginal]);

  // Revoke any outstanding preview blob URL on unmount.
  useEffect(() => () => {
    if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
  }, []);

  // ── Preview zoom + pan (smoke test #2, Bill 2026-06-12) ────────────────
  // Keep the panned image inside the viewport: at zoom z the scaled image is
  // base×z; the max offset from center is half the overhang per axis. When
  // the scaled image fits an axis, that axis snaps back to center (0).
  const clampPan = useCallback(
    (p: { x: number; y: number }, z: number): { x: number; y: number } => {
      if (z <= 1) return { x: 0, y: 0 };
      const vp = zoomViewportRef.current;
      const img = zoomImgRef.current;
      if (!vp || !img) return p;
      const maxX = Math.max(0, (img.offsetWidth * z - vp.clientWidth) / 2);
      const maxY = Math.max(0, (img.offsetHeight * z - vp.clientHeight) / 2);
      return {
        x: Math.max(-maxX, Math.min(maxX, p.x)),
        y: Math.max(-maxY, Math.min(maxY, p.y)),
      };
    },
    [],
  );

  // Re-clamp (and recenter at 1×) whenever the zoom level changes.
  useEffect(() => {
    setPan((p) => clampPan(p, zoom));
  }, [zoom, clampPan]);

  /** +/− controls — step to the next/previous ZOOM_STEPS entry. */
  const stepZoom = useCallback((dir: 1 | -1): void => {
    setZoom((z) => {
      const eps = 0.001;
      if (dir > 0) return ZOOM_STEPS.find((s) => s > z + eps) ?? ZOOM_MAX;
      const below = ZOOM_STEPS.filter((s) => s < z - eps);
      return below.length > 0 ? (below[below.length - 1] as number) : 1;
    });
  }, []);

  /** Click on the image — cycle 1× → 1.5× → 2× → 3× → 4× → back to 1×. */
  const cycleZoom = useCallback((): void => {
    setZoom((z) => ZOOM_STEPS.find((s) => s > z + 0.001) ?? 1);
  }, []);

  // Mouse-wheel zoom over the preview. Native listener with passive:false —
  // React's synthetic onWheel can't preventDefault the dialog scroll.
  useEffect(() => {
    const vp = zoomViewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.25 : 0.8;
      setZoom((z) => Math.min(ZOOM_MAX, Math.max(1, Math.round(z * factor * 100) / 100)));
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
    // The viewport only exists in the review phase with a preview rendered.
  }, [phase, previewUrl]);

  // Drag-to-pan via Pointer Events (mouse + touch + pen). Only active when
  // zoomed in; a real drag suppresses the follow-up click-to-zoom cycle.
  const onZoomPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (zoom <= 1) return;
    if ((e.target as HTMLElement).closest('.pdw-capture-zoombar')) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseX: pan.x,
      baseY: pan.y,
      moved: false,
    };
    setIsPanning(true);
  };
  const onZoomPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    setPan(clampPan({ x: d.baseX + dx, y: d.baseY + dy }, zoom));
  };
  const onZoomPointerEnd = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    suppressClickRef.current = d.moved;
    dragRef.current = null;
    setIsPanning(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* capture already released */
    }
  };
  const onZoomViewClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if ((e.target as HTMLElement).closest('.pdw-capture-zoombar')) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    cycleZoom();
  };

  // Run extraction against captured bytes (optionally classify first).
  const runExtraction = useCallback(
    async (base64: string, mimeType: string, knownType?: string): Promise<void> => {
      setError(null);
      setResult(null);
      // Fresh extraction invalidates prior inline corrections + conflict picks.
      setEdits({});
      setConflictPicks({});
      setPhase('extracting');
      let typeForExtract = knownType ?? '';
      try {
        if (!knownType && autoClassify) {
          const cls = await autoClassify(base64, mimeType);
          typeForExtract = cls.docTypeValue;
          setChosenDocType(cls.docTypeValue);
          setDetectedRawType(cls.detectedRawType ?? cls.docTypeValue);
        } else if (knownType) {
          setChosenDocType(knownType);
        }
        const r = await onCapture(base64, mimeType, typeForExtract || undefined);
        if (!r.success) {
          setError(r.error ?? 'Extraction failed.');
          setResult(r);
          setPhase('failed');
          return;
        }
        // Sideways scans (Bill 2026-10-16): Azure DI read the page at an angle —
        // store and show it upright. Extraction already ran on the original bytes.
        const turn = r.uprightRotationDeg;
        if ((turn === 90 || turn === 180 || turn === 270) && mimeType.startsWith('image/')) {
          try {
            const upright = await rotateImageBase64(base64, mimeType, turn);
            setPages((prev) => prev.map((p) => (p.base64 === base64 ? { ...p, base64: upright.base64, mimeType: upright.mimeType } : p)));
            setFirstOriginal((prev) =>
              prev && prev.base64 === base64 ? { ...prev, base64: upright.base64, mimeType: upright.mimeType } : prev,
            );
          } catch {
            // Rotation is cosmetic for the record — keep the original page on failure.
          }
        }
        setResult(r);
        setPhase('review');
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setPhase('failed');
      }
    },
    [autoClassify, onCapture],
  );

  // A new page of bytes arrived (from upload, drop, scanner, or camera).
  // Extraction runs ONCE — on the first page. Subsequent "Add page" captures
  // append to the merged PDF (§2.3) without clobbering page 1's fields.
  const acceptCapture = useCallback(
    async (base64: string, mimeType: string, filename?: string): Promise<void> => {
      const isFirst = !firstCapturedRef.current;
      setPages((prev) => [...prev, { base64, mimeType }]);
      if (isFirst) {
        firstCapturedRef.current = true;
        setFirstOriginal({ base64, mimeType, ...(filename ? { filename } : {}) });
        await runExtraction(base64, mimeType);
      } else {
        // Appended page — keep the page-1 extraction result; back to review.
        setPhase('review');
      }
    },
    [runExtraction],
  );

  // Upload-tile / drop file handler — preflight then accept.
  const handleFile = useCallback(
    async (file: File): Promise<void> => {
      setError(null);
      try {
        const { base64, mimeType, filename } = await prepareFile(file, maxUploadBytes);
        await acceptCapture(base64, mimeType, filename);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setPhase('failed');
      }
    },
    [acceptCapture, maxUploadBytes],
  );

  // Auto-start from a dropped file handed in by the parent (tile drag-drop).
  const initialHandledRef = useRef<File | null>(null);
  useEffect(() => {
    if (!open) return;
    if (initialFile && initialHandledRef.current !== initialFile) {
      initialHandledRef.current = initialFile;
      void handleFile(initialFile);
    }
  }, [open, initialFile, handleFile]);

  // Reset when the modal closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      reset();
      initialHandledRef.current = null;
    }
  }, [open, reset]);

  // Plain-language, deduplicated, ordered display rows (NRA smoke test —
  // backend keys like fullName/pcbIdNumber/fornTaxId never face the FSR; the
  // pcbId*/id*/raw families collapse to one row per logical fact; MRZ last).
  const displayRows = useMemo<ReadonlyArray<ExtractDisplayRow>>(
    () => (result?.fields ? buildDisplayRows(result.fields) : []),
    [result],
  );
  const primaryRows = useMemo(() => displayRows.filter((r) => !r.secondary), [displayRows]);
  const secondaryRows = useMemo(() => displayRows.filter((r) => r.secondary), [displayRows]);
  const conflicts = result?.conflicts ?? [];
  const unresolvedConflicts = conflicts.filter((c) => !conflictPicks[c.field]);
  // BE-flagged below-confidence-floor fields (incl. heuristic address-letter
  // rows at fixed 0.66) — highlighted so the FSR confirms/corrects at Apply.
  const needsConfirmKeys = useMemo(
    () => new Set((result?.needsConfirm ?? []).map(normalizeNeedsConfirmKey)),
    [result],
  );

  const handleApply = useCallback(async (): Promise<void> => {
    if (!result || !result.success || pages.length === 0 || !firstOriginal) return;
    // Conflict gate — every stated-vs-extracted alert needs an FSR pick
    // before the capture can apply (never silent-overwrite, §2.6).
    if ((result.conflicts ?? []).some((c) => !conflictPicks[c.field])) return;
    setPhase('saving');
    try {
      // Officer corrections win over the raw extract (D1 — fix at the Apply
      // gate). Fan edits out to every underlying member key, fold in the
      // document-side conflict picks, and rebuild the fields map so hosts
      // (drawer cache, comparators) see the corrected values.
      const rows = result.fields ? buildDisplayRows(result.fields) : [];
      const corrections = correctionsForEdits(rows, edits);
      for (const c of result.conflicts ?? []) {
        if (conflictPicks[c.field] === 'extracted') corrections[c.field] = c.extractedPatchValue;
      }
      let appliedResult: ScanResultPayload = result;
      if (Object.keys(corrections).length > 0) {
        const correctedFields = { ...(result.fields ?? {}) };
        for (const row of rows) {
          const edited = edits[row.factId];
          if (edited === undefined || edited.trim() === row.value) continue;
          for (const member of row.memberKeys) {
            // Officer-typed → no OCR score; renders as "manual", never a
            // fabricated percentage (confidence-integrity rule 2026-06-03).
            correctedFields[member] = { value: edited.trim(), confidence: -1 };
          }
        }
        appliedResult = { ...result, fields: correctedFields, corrections };
      }
      let artifact: CaptureArtifact;
      if (reviewOf && pages.length === 1) {
        // Review import: same document, new corrections — keep every original page.
        artifact = reviewOf.artifact;
      } else if (pdfOnSave) {
        const merged = await mergeCapturesToPdf(pages);
        artifact = {
          pdfBase64: merged.base64,
          pdfMimeType: 'application/pdf',
          pageCount: merged.pageCount,
          originalBase64: firstOriginal.base64,
          originalMimeType: firstOriginal.mimeType,
          ...(firstOriginal.filename ? { originalFilename: firstOriginal.filename } : {}),
        };
      } else {
        artifact = {
          pdfBase64: firstOriginal.base64,
          pdfMimeType: 'application/pdf',
          pageCount: pages.length,
          originalBase64: firstOriginal.base64,
          originalMimeType: firstOriginal.mimeType,
          ...(firstOriginal.filename ? { originalFilename: firstOriginal.filename } : {}),
        };
      }
      onApply(appliedResult, artifact, chosenDocType || undefined);
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save document as PDF.');
      setPhase('review');
    }
  }, [result, pages, firstOriginal, pdfOnSave, onApply, chosenDocType, reset, onClose, edits, conflictPicks, reviewOf]);

  const handleCancel = useCallback((): void => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleRescan = useCallback((): void => {
    // Discard captured pages + results; back to the chooser.
    setPhase('choose');
    setDeviceMode('none');
    setResult(null);
    setError(null);
    setPages([]);
    setFirstOriginal(null);
    setDetectedRawType(null);
    setEdits({});
    setConflictPicks({});
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsPanning(false);
    firstCapturedRef.current = false;
  }, []);

  // Override changed in review → re-extract the FIRST page with the new type.
  const handleOverrideChange = useCallback(
    (next: string): void => {
      setChosenDocType(next);
      const page = pages[0];
      if (page) void runExtraction(page.base64, page.mimeType, next);
    },
    [pages, runExtraction],
  );

  if (!open) return null;

  const iconUrl = iconSlug ? documentIconUrl(iconSlug) : undefined;
  const busy = phase === 'extracting' || phase === 'saving';
  const deviceTileLabel = isMobile ? 'Camera' : 'Scanner';
  const deviceTabs: ReadonlyArray<'scanner' | 'camera'> = isMobile ? ['camera'] : ['scanner'];

  // Action buttons live in the modal HEADER (smoke test #2, Bill 2026-06-12)
  // — the old footer row forced the review content to scroll before the FSR
  // could Cancel / Re-scan / Apply. Same buttons, same gating, new home.
  let headerActions: ReactNode = null;
  if (phase === 'review') {
    headerActions = (
      <>
        <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
        <Button variant="ghost" onClick={handleRescan}>Re-scan</Button>
        <Button
          variant="primary"
          onClick={() => { void handleApply(); }}
          disabled={unresolvedConflicts.length > 0}
          title={unresolvedConflicts.length > 0
            ? 'Resolve the highlighted mismatch first — choose which value to keep'
            : 'Save the values shown (including your corrections) and attach the document'}
        >
          Apply
        </Button>
      </>
    );
  } else if (phase === 'failed') {
    headerActions = (
      <>
        <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
        <Button variant="primary" onClick={handleRescan}>Try again</Button>
      </>
    );
  } else if (phase === 'choose') {
    headerActions = <Button variant="ghost" onClick={handleCancel}>Cancel</Button>;
  } else {
    headerActions = <Button variant="ghost" onClick={handleCancel} disabled>Working…</Button>;
  }

  // Fragility guard (NRA smoke test, Bill 2026-06-12) — while a file is
  // captured / extraction is running / unsaved corrections exist, a stray
  // outside-click, Escape, or the header × must confirm before discarding.
  const hasUnsavedWork =
    pages.length > 0
    || phase === 'extracting'
    || phase === 'review'
    || phase === 'saving'
    || Object.keys(edits).length > 0;

  // Close-request router — every close path (header ×, Escape, scrim) gates
  // through the discard confirm when there is unsaved work. Identical
  // semantics to ModalShell's confirmClose guard, hosted locally because the
  // capture modal renders its own header bar (noHeader on the shell).
  const requestClose = (): void => {
    if (hasUnsavedWork) setPendingClose(true);
    else handleCancel();
  };

  return (
    <ModalShell
      open={open}
      onClose={requestClose}
      title={`Capture · ${docLabel}`}
      ariaLabel={`Capture · ${docLabel}`}
      noHeader
      size={phase === 'review' ? 'auto' : 'md'}
      disableBackdropClose={busy}
      disableEscapeClose={busy}
    >
      <div className="pdw-capture-modal" data-mode={mode}>
        {/* Header bar — title + relocated action buttons + close ×. Sticky so
            the actions stay reachable even when the review content scrolls. */}
        <div className="pdw-capture-headbar">
          <h2 className="pdw-capture-headbar__title">Capture · {docLabel}</h2>
          <div className="pdw-capture-headbar__actions">{headerActions}</div>
          <button
            type="button"
            className="pdw-capture-headbar__close"
            onClick={requestClose}
            aria-label="Close"
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        {docHint && <p className="pdw-capture-modal__hint">{docHint}</p>}

        <div className="pdw-capture-modal__head">
          <div className="pdw-capture-modal__thumb" aria-hidden>
            {iconUrl
              ? <img src={iconUrl} alt="" width={48} height={48} />
              : <span className="pdw-capture-modal__thumb-fallback">{docLabel.charAt(0)}</span>}
          </div>
          <div className="pdw-capture-modal__status">
            <strong>
              {phase === 'choose' && 'Choose how to capture this document.'}
              {phase === 'extracting' && 'Reading the document with Azure Document Intelligence…'}
              {phase === 'review' && 'Review the extracted fields, then Apply.'}
              {phase === 'saving' && 'Saving as PDF…'}
              {phase === 'failed' && 'Capture failed — try again or pick a different document.'}
            </strong>
            <span className="pdw-capture-modal__status-sub">
              Typically 3–6 seconds. PDF · JPEG · PNG · TIFF · HEIC · HEIF — max 25 MB (large files auto-resize).
            </span>
          </div>
        </div>

        {error && <Banner tone="err">{error}</Banner>}

        {/* Attach-only doc types skip the scan-time screening, so state the
            military-ID restriction plainly (18 USC 701) before the FSR
            uploads or applies. Informational, not a blocker. */}
        {attachOnly && (phase === 'choose' || phase === 'review') && (
          <p className="pdw-capture-modal__attachnote">
            This document attaches as-is — nothing is read automatically.
            US Military IDs cannot be imaged or stored — record those details manually instead.
          </p>
        )}

        {/* Phase: chooser — two tiles. Drag-drop is accepted ANYWHERE on the
            whole chooser area (Bill 2026-06-01), not just the File-Upload tile. */}
        {phase === 'choose' && deviceMode === 'none' && (
          <div
            className={`pdw-capture-chooser${dragOver ? ' pdw-capture-chooser--dragover' : ''}`}
            role="group"
            aria-label="Capture method"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true); }}
            onDragLeave={(e) => {
              // Only clear when the cursor actually leaves the chooser (not when
              // moving between the inner tiles).
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) void handleFile(f);
            }}
          >
            <button
              type="button"
              className="pdw-capture-chooser__tile"
              onClick={() => setDeviceMode('device')}
            >
              <span className="pdw-capture-chooser__icon" aria-hidden>{isMobile ? '📷' : '🖨️'}</span>
              <span className="pdw-capture-chooser__label">{deviceTileLabel}</span>
              <span className="pdw-capture-chooser__sub">
                {isMobile ? 'Use this device camera' : 'Use the connected document scanner'}
              </span>
            </button>
            {/* Drop is handled by the parent chooser (drop-anywhere); this tile
                keeps click/keyboard → browse. */}
            <div
              role="button"
              tabIndex={0}
              className="pdw-capture-chooser__tile"
              onClick={() => uploadInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); uploadInputRef.current?.click(); }
              }}
            >
              <span className="pdw-capture-chooser__icon" aria-hidden>📄</span>
              <span className="pdw-capture-chooser__label">File Upload</span>
              <span className="pdw-capture-chooser__sub">Drag &amp; drop a file anywhere here, or click to browse</span>
            </div>
            <input
              ref={uploadInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              title="Select document"
              className="pdw-visually-hidden-input"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) void handleFile(f);
              }}
            />
          </div>
        )}

        {/* Phase: chooser → device (scanner/camera) capture surface. */}
        {phase === 'choose' && deviceMode === 'device' && (
          <div className="pdw-capture-modal__device">
            <DocumentCaptureModule
              tabs={deviceTabs}
              onCapture={(b64, mime) => { void acceptCapture(b64, mime); }}
              maxUploadBytes={maxUploadBytes}
            />
            <button type="button" className="pdw-capture-modal__back" onClick={() => setDeviceMode('none')}>
              ← Back to capture options
            </button>
          </div>
        )}

        {/* Phase: extracting / saving — honest indeterminate status (no fake chips). */}
        {(phase === 'extracting' || phase === 'saving') && (
          <ProcessingHero phase={phase} firstOriginal={firstOriginal} detectedRawType={detectedRawType} />
        )}

        {/* Phase: review — the captured document ENLARGED beside the values
            read from it (NRA smoke test, Bill 2026-06-12), with inline
            corrections, plain-language deduplicated labels, conflict picks,
            and the MRZ collapsed at the bottom. */}
        {phase === 'review' && result && (
          <div className="pdw-capture-modal__review">
            <div className="pdw-capture-compare">
              {/* Left — the captured document, large enough to read. Multi-step
                  zoom (click cycles, +/− steps, mouse wheel) with drag-to-pan
                  when zoomed (smoke test #2, Bill 2026-06-12 — book-style
                  passports need a deep zoom into the bottom half). */}
              <figure className="pdw-capture-compare__doc">
                {previewUrl ? (
                  <div
                    ref={zoomViewportRef}
                    className={[
                      'pdw-capture-zoomview',
                      zoom > 1 ? 'pdw-capture-zoomview--pan' : '',
                      isPanning ? 'pdw-capture-zoomview--panning' : '',
                    ].filter(Boolean).join(' ')}
                    style={{ touchAction: zoom > 1 ? 'none' : undefined }}
                    onPointerDown={onZoomPointerDown}
                    onPointerMove={onZoomPointerMove}
                    onPointerUp={onZoomPointerEnd}
                    onPointerCancel={onZoomPointerEnd}
                    onClick={onZoomViewClick}
                    title={zoom > 1
                      ? 'Drag to pan — click for the next zoom step, scroll wheel to zoom'
                      : 'Click or scroll to zoom'}
                  >
                    <div className="pdw-capture-zoombar" role="group" aria-label="Preview zoom">
                      <button
                        type="button"
                        className="pdw-capture-zoombar__btn"
                        onClick={() => stepZoom(-1)}
                        disabled={zoom <= 1}
                        aria-label="Zoom out"
                        title="Zoom out"
                      >
                        −
                      </button>
                      <span className="pdw-capture-zoombar__level" aria-live="polite">
                        {Math.round(zoom * 100)}%
                      </span>
                      <button
                        type="button"
                        className="pdw-capture-zoombar__btn"
                        onClick={() => stepZoom(1)}
                        disabled={zoom >= ZOOM_MAX}
                        aria-label="Zoom in"
                        title="Zoom in"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        className="pdw-capture-zoombar__btn pdw-capture-zoombar__btn--fit"
                        onClick={() => setZoom(1)}
                        disabled={zoom <= 1}
                        aria-label="Fit to pane"
                        title="Fit to pane"
                      >
                        Fit
                      </button>
                    </div>
                    <img
                      ref={zoomImgRef}
                      src={previewUrl}
                      alt={`Captured ${docLabel}`}
                      draggable={false}
                      style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transition: isPanning ? 'none' : 'transform 120ms ease',
                      }}
                    />
                  </div>
                ) : (
                  <div className="pdw-capture-compare__noprev">
                    Preview is not available for this file — the document still attaches and saves as a PDF on Apply.
                  </div>
                )}
                <figcaption>
                  Captured document{pages.length > 1 ? ` · page 1 of ${pages.length}` : ''} — compare against
                  the values on the right. Click or scroll to zoom; drag to pan when zoomed.
                </figcaption>
              </figure>

              {/* Right — override, conflicts, correctable values. */}
              <div className="pdw-capture-compare__fields">
                {overrideOptions && overrideOptions.length > 0 && (
                  <div className="pdw-capture-modal__detected">
                    <label className="pdw-capture-modal__detected-label" htmlFor="pdw-capture-override">
                      {detectedRawType
                        ? <>Detected as <strong>{detectedRawType}</strong> — confirm or override</>
                        : <>Confirm the document type</>}
                    </label>
                    <select
                      id="pdw-capture-override"
                      className="pdw-capture-modal__override"
                      value={chosenDocType}
                      onChange={(e) => handleOverrideChange(e.currentTarget.value)}
                    >
                      {overrideOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Stated-vs-document mismatches — the FSR picks a side
                    before Apply; the stated value is never silently replaced. */}
                {conflicts.map((c) => {
                  const pick = conflictPicks[c.field];
                  return (
                    <div
                      key={c.field}
                      role="alert"
                      className={`pdw-capture-conflict${pick ? ' pdw-capture-conflict--resolved' : ''}`}
                    >
                      <div className="pdw-capture-conflict__title">
                        Possible mismatch — {c.label}
                      </div>
                      <div className="pdw-capture-conflict__body">
                        The profile shows <b>{c.stated}</b> but this document reads <b>{c.extracted}</b>.
                        Choose which value to keep.
                      </div>
                      <div className="pdw-capture-conflict__actions">
                        <button
                          type="button"
                          className={`pdw-capture-conflict__btn${pick === 'stated' ? ' pdw-capture-conflict__btn--picked' : ''}`}
                          aria-pressed={pick === 'stated' ? 'true' : 'false'}
                          onClick={() => setConflictPicks((prev) => ({ ...prev, [c.field]: 'stated' }))}
                        >
                          Keep {c.stated}
                        </button>
                        <button
                          type="button"
                          className={`pdw-capture-conflict__btn${pick === 'extracted' ? ' pdw-capture-conflict__btn--picked' : ''}`}
                          aria-pressed={pick === 'extracted' ? 'true' : 'false'}
                          onClick={() => setConflictPicks((prev) => ({ ...prev, [c.field]: 'extracted' }))}
                        >
                          Use document value
                        </button>
                      </div>
                    </div>
                  );
                })}

                {!attachOnly && (
                <div className="pdw-capture-modal__results-h">
                  Values read from the document
                  {result.needsConfirm && result.needsConfirm.length > 0 && (
                    <span className="pdw-capture-modal__results-warn"> · {result.needsConfirm.length} need confirm</span>
                  )}
                  <span className="pdw-capture-modal__results-hint">Correct any value before Apply.</span>
                </div>
                )}
                {primaryRows.length > 0 ? (
                  <ul className="pdw-capture-modal__results-list">
                    {primaryRows.map((row) => {
                      const edited = edits[row.factId];
                      const isEdited = edited !== undefined && edited.trim() !== row.value;
                      const needsAttention = !isEdited && (
                        needsConfirmKeys.has(row.factId)
                        || row.memberKeys.some((k) => needsConfirmKeys.has(k))
                      );
                      return (
                        <li
                          key={row.factId}
                          className={`pdw-capture-modal__results-row pdw-capture-modal__results-row--edit${
                            needsAttention ? ' pdw-capture-modal__results-row--confirm' : ''
                          }`}
                        >
                          <label className="pdw-capture-modal__results-key" htmlFor={`pdw-capf-${row.factId}`}>
                            {row.label}
                          </label>
                          {row.editable ? (
                            <input
                              id={`pdw-capf-${row.factId}`}
                              className="pdw-capture-modal__results-input"
                              value={edited ?? row.value}
                              onChange={(e) => {
                                const v = e.currentTarget.value;
                                setEdits((prev) => ({ ...prev, [row.factId]: v }));
                              }}
                              title={`${row.label} — correct if the document reads differently`}
                            />
                          ) : (
                            <span className="pdw-capture-modal__results-val">{row.displayValue}</span>
                          )}
                          {isEdited ? (
                            <span
                              className="pdw-capture-modal__results-conf pdw-capture-modal__results-conf--edited"
                              title="You corrected this value — it will be saved instead of the scanned one"
                            >
                              edited
                            </span>
                          ) : (
                            <span
                              className={`pdw-capture-modal__results-conf pdw-capture-modal__results-conf--${
                                row.confidence < 0 ? 'manual'
                                  : row.confidence >= 0.9 ? 'hi' : row.confidence >= 0.7 ? 'med' : 'lo'
                              }`}
                              title={row.missing
                                ? 'Not read from the document — type it from the ID if it is there'
                                : row.confidence < 0
                                ? 'Entered or derived — not read off the document'
                                : `Read from the document — ${Math.round(row.confidence * 100)}% confidence`}
                            >
                              {row.missing ? 'not read' : row.confidence < 0 ? 'manual' : `${Math.round(row.confidence * 100)}%`}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : !attachOnly ? (
                  <p className="pdw-capture-modal__results-empty">
                    No fields could be read — the document will still attach. Re-scan for a clearer image if needed.
                  </p>
                ) : null}

                {/* Raw machine-readable zone — collapsed, never a peer row. */}
                {secondaryRows.length > 0 && (
                  <details className="pdw-capture-modal__mrz">
                    <summary>Machine-readable zone (MRZ)</summary>
                    {secondaryRows.map((row) => (
                      <code key={row.factId} className="pdw-capture-modal__mrz-val">{row.value}</code>
                    ))}
                  </details>
                )}

                {/* Multipage — preserve all pages in one PDF (§2.3). */}
                <div className="pdw-capture-modal__pages">
                  <span>{pages.length} page{pages.length === 1 ? '' : 's'} captured</span>
                  <button
                    type="button"
                    className="pdw-capture-modal__addpage"
                    onClick={() => { setPhase('choose'); setDeviceMode('none'); }}
                    title="Capture another page — appended to the same PDF"
                  >
                    + Add another page
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discard-confirm — same guard ModalShell's confirmClose provided
            (positive confirm before a stray close destroys a capture or
            unsaved corrections). Hosted locally; copy + buttons unchanged. */}
        {pendingClose && (
          <div
            role="presentation"
            className="pdw-capture-confirm-scrim"
            onClick={(e) => { if (e.target === e.currentTarget) setPendingClose(false); }}
          >
            <div role="alertdialog" aria-modal="true" className="pdw-capture-confirm">
              <h3>Discard this capture?</h3>
              <p>
                The captured document and any corrections have not been applied yet.
                Closing now will discard them.
              </p>
              <div className="pdw-capture-confirm__actions">
                <button
                  type="button"
                  className="pdw-capture-confirm__keep"
                  onClick={() => setPendingClose(false)}
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  className="pdw-capture-confirm__discard"
                  onClick={() => { setPendingClose(false); handleCancel(); }}
                >
                  Discard changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}
